
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Code, Copy, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface GeneratedCodeDisplayProps {
  generatedCode: string;
}

const GeneratedCodeDisplay: React.FC<GeneratedCodeDisplayProps> = ({ generatedCode }) => {
  const { toast } = useToast();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedCode);
    toast({
      title: "Code Copied",
      description: "The agent code has been copied to your clipboard"
    });
  };

  return (
    <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Code className="h-5 w-5 text-[#0EA5E9]" />
          Generated Code
        </CardTitle>
        <CardDescription>
          Your agent implementation
        </CardDescription>
      </CardHeader>
      <CardContent>
        {generatedCode ? (
          <>
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-md overflow-auto max-h-[500px] text-sm">
                <code>{generatedCode}</code>
              </pre>
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 text-white/70 hover:text-white hover:bg-white/10"
                onClick={copyToClipboard}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-4 space-y-4">
              <p className="text-sm text-gray-600">
                This code demonstrates how to implement your agent. You can customize it further to fit your specific needs.
              </p>
              <div className="flex space-x-2">
                <Button className="bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90" onClick={copyToClipboard}>
                  <Copy className="h-4 w-4 mr-2" /> Copy Code
                </Button>
                <Button variant="outline" className="border-[#0EA5E9]/30">
                  <ExternalLink className="h-4 w-4 mr-2" /> Deploy
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-[500px] text-gray-400">
            <div className="text-center">
              <Code className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>Select a template and generate your agent to see the code here</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GeneratedCodeDisplay;
